import LocalizedLink from "@/components/LocalizedLink";
import NextArrow from "@/components/NextArrow";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * `/support` (EN) and `/ar/support` (AR).
 *
 * Support Center hub. A short, human-feeling page that explains *what*
 * support means in the SAS context, and lists the three concrete routes
 * a visitor can take from here:
 *
 *   1. Request Support   → /support/request (the bilingual form)
 *   2. Contact Methods   → /contact         (the existing hub with all
 *                                            available channels)
 *   3. SAS Community     → /community       (a clean placeholder until
 *                                            community channels are
 *                                            ready to publish)
 *
 * Visual language mirrors the rest of the launch homepage: centred
 * heading, three equal "card" links, no decorative noise.
 */

const C = {
  ar: {
    eyebrow: "الدعم",
    title: "مركز الدعم",
    lead:
      "نحن هنا لمساعدتك بأسرع وقت ممكن. اختر الطريقة التي تناسبك للوصول إلينا، سواء عبر إرسال طلب دعم رسمي، أو من خلال قنوات التواصل المباشرة، أو بالانضمام إلى مجتمع SAS.",
    cards: [
      {
        eyebrow: "طلب دعم",
        title: "إرسال طلب دعم",
        body:
          "إذا واجهتك مشكلة تقنية أو سؤال محدد حول الأدوات والورشات، يمكنك إرسال طلب رسمي وسنرد عليك من خلال طريقة التواصل التي تختارها.",
        cta: "افتح نموذج الدعم",
        to: "/support/request",
        testid: "support-card-request",
      },
      {
        eyebrow: "التواصل",
        title: "طرق التواصل",
        body:
          "صفحة موحّدة تعرض جميع طرق التواصل مع SAS — للشراكات، الورشات، الأسئلة العامة، والتطوع — مع روابط مباشرة لكل نموذج.",
        cta: "تصفّح طرق التواصل",
        to: "/contact",
        testid: "support-card-contact",
      },
      {
        eyebrow: "المجتمع",
        title: "مجتمع SAS",
        body:
          "نعمل على إعداد قنوات مجتمعية تجمع الطلاب والباحثين والمهتمين بالذكاء الاصطناعي. ستتوفر تفاصيل الانضمام قريبًا.",
        cta: "تعرّف على المجتمع",
        to: "/community",
        testid: "support-card-community",
      },
    ],
    closing:
      "كل طلب دعم يصلنا يخضع لمراجعة فريق SAS، وسنرد عليكم عبر طريقة التواصل التي تختارونها في النموذج.",
  },
  en: {
    eyebrow: "Support",
    title: "Support Center",
    lead:
      "We are here to help as quickly as we can. Pick the channel that fits you best — open a formal support request, browse our contact methods, or join the SAS community.",
    cards: [
      {
        eyebrow: "Request",
        title: "Request Support",
        body:
          "If you ran into a technical issue, or have a specific question about the tools or workshops, send us a formal request and we will reply through your preferred channel.",
        cta: "Open the support form",
        to: "/support/request",
        testid: "support-card-request",
      },
      {
        eyebrow: "Reach us",
        title: "Contact Methods",
        body:
          "A single page that lists every way to reach SAS — partnerships, workshops, general inquiries, and volunteering — with a direct link to each form.",
        cta: "Browse contact methods",
        to: "/contact",
        testid: "support-card-contact",
      },
      {
        eyebrow: "Community",
        title: "SAS Community",
        body:
          "We are setting up community channels for students, researchers, and AI enthusiasts. Joining details will be published shortly.",
        cta: "About the community",
        to: "/community",
        testid: "support-card-community",
      },
    ],
    closing:
      "Every support request is reviewed by the SAS team, and we will reply through the contact method you choose in the form.",
  },
};

export default function Support() {
  const { lang } = useLanguage();
  const c = C[lang];

  return (
    <section className="sas-section" data-testid="support-page">
      <div className="sas-container">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="sas-eyebrow mb-4" data-testid="support-eyebrow">
            {c.eyebrow}
          </div>
          <h1 className="sas-h2" data-testid="support-title">
            {c.title}
          </h1>
          <p className="sas-body mt-5 text-[16px] leading-[1.85] max-w-2xl mx-auto">
            {c.lead}
          </p>
        </div>

        {/* Three equal cards. Whole card is clickable. */}
        <div className="sas-grid-3 mt-12">
          {c.cards.map((card) => (
            <LocalizedLink
              key={card.testid}
              to={card.to}
              className="
                sas-card flex flex-col h-full transition-colors
                hover:border-[color:var(--sas-accent)]
                focus-visible:outline-none focus-visible:ring-2
                focus-visible:ring-offset-2
                focus-visible:ring-[color:var(--sas-accent)]
              "
              data-testid={card.testid}
            >
              <span className="sas-chip mb-4">{card.eyebrow}</span>
              <h3 className="sas-h3">{card.title}</h3>
              <p className="sas-body mt-3 text-[15px] leading-[1.85] flex-1">
                {card.body}
              </p>
              <span className="sas-btn-link mt-6 self-start">
                {card.cta} <NextArrow size={16} />
              </span>
            </LocalizedLink>
          ))}
        </div>

        {/* Reassuring closing note. */}
        <p
          className="
            sas-body mt-12 max-w-2xl mx-auto text-center text-[14px]
            text-[color:var(--sas-ink-2)] leading-[1.85]
          "
          data-testid="support-closing"
        >
          {c.closing}
        </p>
      </div>
    </section>
  );
}
